import { styled, alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

const StyledCalendar = styled('div')(({ theme }) => ({
  width: 'calc(100% + 2px)',
  marginLeft: -1,
  marginBottom: -1,

  // React Big Calendar Main Styles
  '& .rbc-calendar': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },

  // Header Styles
  '& .rbc-header': {
    ...theme.typography.subtitle2,
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    color: theme.palette.text.primary,
    fontWeight: 600,
  },

  '& .rbc-time-header': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
  },

  '& .rbc-time-header-content': {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  // Today styling
  '& .rbc-today': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.08),
  },

  // Event styles
  '& .rbc-event': {
    borderRadius: 6,
    border: 'none',
    padding: '0px 6px',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: theme.transitions.create(['filter', 'box-shadow']),
    boxShadow: theme.palette.mode === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.3)'
      : '0 1px 2px rgba(0,0,0,0.1)',
    '&:hover': {
      filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'brightness(0.9)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 6px rgba(0,0,0,0.4)'
        : '0 2px 4px rgba(0,0,0,0.15)',
    },
  },

  '& .rbc-event-content': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Time grid styles
  '& .rbc-time-view': {
    border: `1px solid ${theme.palette.divider}`,
  },

  '& .rbc-time-gutter': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    borderRight: `1px solid ${theme.palette.divider}`,
  },

  // Time column styling
  '& .rbc-time-column': {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  // Time header gutter
  '& .rbc-time-header-gutter': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
  },

  // Label styling
  '& .rbc-label': {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.caption.fontWeight,
  },

  '& .rbc-timeslot-group': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  },

  '& .rbc-time-slot': {
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  },

  // Month view styles
  '& .rbc-month-view': {
    border: `1px solid ${theme.palette.divider}`,
  },

  '& .rbc-month-row': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  '& .rbc-date-cell': {
    borderRight: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    '&.rbc-off-range': {
      color: theme.palette.text.disabled,
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.grey[100],
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },

  '& .rbc-day-bg': {
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },

  // Week view styles
  '& .rbc-time-content': {
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  // Agenda view styles
  '& .rbc-agenda-view': {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '& table': {
      backgroundColor: theme.palette.background.paper,
      '& th, & td': {
        borderColor: 'transparent',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
      },
      '& tbody tr': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  },

  '& .rbc-agenda-date-cell': {
    ...theme.typography.subtitle2,
    color: theme.palette.text.primary,
    fontWeight: 600,
  },

  '& .rbc-agenda-time-cell': {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  },

  '& .rbc-agenda-event-cell': {
    ...theme.typography.body2,
    '& .rbc-event': {
      border: 'none !important',
      borderRadius: '4px !important',
      padding: '2px 6px !important',
      fontSize: '0.75rem !important',
      fontWeight: '500 !important',
      display: 'inline-block !important',
      maxWidth: '100% !important',
      color: '#fff !important',
      textDecoration: 'none !important',
      // NÃO sobrescrever backgroundColor - deixar para o eventPropGetter
      // backgroundColor será aplicado via style inline pelo eventPropGetter
      boxShadow: theme.palette.mode === 'dark'
        ? '0 1px 2px rgba(0,0,0,0.3) !important'
        : '0 1px 2px rgba(0,0,0,0.1) !important',
      '&:hover': {
        textDecoration: 'none !important',
        filter: theme.palette.mode === 'dark' ? 'brightness(1.2) !important' : 'brightness(0.9) !important',
      },
    },
    '& .rbc-event-content': {
      color: '#fff !important',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Garantir que estilos inline sejam preservados
    '& .rbc-event[style]': {
      backgroundColor: 'inherit !important',
    },
  },

  // Forçar estilos inline na agenda view
  '& .rbc-agenda-view': {
    '& .rbc-event[style*="background-color"]': {
      borderRadius: '4px !important',
      padding: '2px 6px !important',
      fontSize: '0.75rem !important',
      fontWeight: '500 !important',
      color: '#fff !important',
    },
  },

  // Show more styles
  '& .rbc-show-more': {
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },

  // Selection styles
  '& .rbc-slot-selection': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 4,
  },

  // Current time indicator
  '& .rbc-current-time-indicator': {
    backgroundColor: theme.palette.error.main,
    height: 2,
    zIndex: 3,
    boxShadow: `0 0 4px ${theme.palette.error.main}`,
  },

  // Resource header styles
  '& .rbc-resource-header': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
  },

  // Resource content
  '& .rbc-resource-content': {
    borderRight: `1px solid ${theme.palette.divider}`,
  },

  // All day event area
  '& .rbc-allday-cell': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.grey[50],
  },

  // All day row header
  '& .rbc-row-header': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[50],
    color: theme.palette.text.secondary,
    borderRight: `1px solid ${theme.palette.divider}`,
  },

  // Time slot background
  '& .rbc-day-slot .rbc-time-slot': {
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  },

  // Gutter text styling
  '& .rbc-time-gutter .rbc-label': {
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
  },

  // Selected event
  '& .rbc-event.rbc-selected': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },

  // Toolbar (disabled)
  '& .rbc-toolbar': {
    display: 'none',
  },

  // Remove outline on focus
  '& .rbc-calendar *:focus': {
    outline: 'none',
  },

  // Additional dark/light theme improvements
  '& .rbc-time-slot': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },

  '& .rbc-day-slot': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },

  // Weekend styling (optional enhancement)
  '& .rbc-off-range-bg': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.grey[50],
  },

  // Month view event overlay
  '& .rbc-month-view .rbc-event': {
    boxShadow: theme.palette.mode === 'dark'
      ? '0 1px 2px rgba(0,0,0,0.4)'
      : '0 1px 2px rgba(0,0,0,0.1)',
    height: '25px !important',
    minHeight: '25px !important',
    lineHeight: '16px',
    fontSize: '0.7rem',
    padding: '1px 4px',
    marginBottom: '1px',
  },

  // Loading state styles
  '& .rbc-loading': {
    opacity: 0.6,
    pointerEvents: 'none',
  },
}));

export default StyledCalendar;
